import React from 'react';
import cn from 'classnames';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { PrimaryButtonLightBg } from 'apps/sigma2-ui/src/components/PrimaryButtonLightBg';
import { SecondaryButtonLightBg } from 'apps/sigma2-ui/src/components/SecondaryButtonLightBg';
import { Spinner } from 'apps/sigma2-ui/src/components/ui/Spinner';
import { DashboardSummaryAlertFilledIconSvg } from 'apps/sigma2-ui/src/components/svg/DashboardSummaryAlertFilledIcon';
import { DashboardSummarySparkleIconSvg } from 'apps/sigma2-ui/src/components/svg/DashboardSummarySparkleIcon';
import { DashboardSummaryModalIconSvg } from 'apps/sigma2-ui/src/components/svg/DashboardSummaryModalIcon';
import { DashboardSummaryErrorDocumentIconSvg } from 'apps/sigma2-ui/src/components/svg/DashboardSummaryErrorDocumentIcon';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { CircularProgress } from '@mui/material';
import { DashboardAISummaryFailureStatus } from 'libs/end-to-end/src/dashboard-ai-summary/dashboard-ai-summary-types';
import { DashboardAISummaryLoadingStatus } from 'libs/end-to-end/src/dashboard-ai-summary/dashboard-ai-summary-types';

export type DashboardAISummaryControlStatus =
  | 'disabled'
  | 'idle'
  | 'loading'
  | 'error';

export interface DashboardAISummaryControlProps {
  status: DashboardAISummaryControlStatus;
  open: boolean;
  onToggle: () => void;
  context?: string;
  onContextChange?: (value: string) => void;
  onSubmit?: () => void;
  onCancel?: () => void;
  onRetry?: () => void;
  onStartOver?: () => void;
  errorStatus?: DashboardAISummaryFailureStatus;
  loadingStatus?: DashboardAISummaryLoadingStatus;
}

const getSummaryToggleLabel = (
  language: ReturnType<typeof useLanguage>,
  status: DashboardAISummaryControlStatus,
) => {
  return status === 'loading'
    ? language.dashboardSummaryLoadingToggleLabel()
    : language.dashboardSummaryToggleLabel();
};

const isSummaryDisabled = (status: DashboardAISummaryControlStatus) => {
  return status === 'disabled';
};

const getToggleContext = (props: DashboardAISummaryControlProps) => {
  if ('context' in props) {
    return props.context ?? '';
  }

  return '';
};

const noop = () => {};

const SummaryStateIcon = ({
  status,
}: {
  status: DashboardAISummaryControlStatus;
}) => {
  if (status === 'loading') {
    return <CircularProgress size={16} className="text-royal-800" />;
  }

  if (status === 'error') {
    return (
      <DashboardSummaryAlertFilledIconSvg svg={{ className: 'h-4 w-4' }} />
    );
  }

  return (
    <DashboardSummarySparkleIconSvg
      svg={{ className: 'h-4 w-4 text-royal-800' }}
      wantsCurrentColor={true}
    />
  );
};

export const DashboardAISummaryControl: React.FC<
  DashboardAISummaryControlProps
> = (props) => {
  const status = props.status;
  const open = props.open;
  const context = getToggleContext(props);
  const hasContext = context.trim().length > 0;
  const language = useLanguage();
  const toggleLabel = getSummaryToggleLabel(language, status);
  const disabled = isSummaryDisabled(status);

  const panel = !props.open ? null : props.status === 'loading' ? (
    <SummaryLoadingPanel
      loadingStatus={props.loadingStatus ?? 'pending'}
      onCancel={props.onCancel ?? noop}
    />
  ) : props.status === 'error' ? (
    <SummaryErrorPanel
      errorStatus={props.errorStatus ?? 'failed-generating-report'}
      onRetry={props.onRetry ?? noop}
      onStartOver={props.onStartOver ?? noop}
    />
  ) : (
    <SummaryFormPanel
      context={props.context ?? ''}
      maxLength={DEFAULT_MAX_LENGTH}
      onContextChange={props.onContextChange ?? noop}
      onSubmit={props.onSubmit ?? noop}
    />
  );

  return (
    <div className="relative flex items-center justify-center">
      <TouchTarget
        disabled={disabled}
        label={toggleLabel}
        behavior={{
          type: 'onClick',
          onClick: props.onToggle,
        }}
        content={({ hovered: internalHovered }) => {
          const isHovered = internalHovered;
          const toggleSurfaceClass = getToggleSurfaceClass({
            disabled,
            open,
            isHovered,
            hasContext,
          });

          return (
            <div
              className={cn(
                'flex items-center gap-3 rounded border-[1.5px] px-4 py-2 transition-colors',
                toggleSurfaceClass,
                {
                  'cursor-not-allowed opacity-[0.32]': disabled,
                  'cursor-pointer': !disabled,
                },
              )}
            >
              <SummaryStateIcon status={status} />
              <S2Typography
                as="span"
                translatedContent={toggleLabel}
                variant="body-7-bold"
                textColorVariant="royal-800"
              />
            </div>
          );
        }}
      />
      {panel && (
        <div className="absolute left-0 top-full z-20 mt-2 min-[1300px]:left-1/2 min-[1300px]:-translate-x-1/2">
          {panel}
        </div>
      )}
    </div>
  );
};

const DEFAULT_MAX_LENGTH = 900;

const getToggleSurfaceClass = ({
  disabled,
  open,
  isHovered,
  hasContext,
}: {
  disabled: boolean;
  open: boolean;
  isHovered: boolean;
  hasContext: boolean;
}) => {
  if (disabled) {
    return 'border-indigo-200 bg-white';
  }

  if (open) {
    return isHovered || hasContext
      ? 'border-indigo-200 bg-indigo-200'
      : 'border-indigo-200 bg-[#E5E5FF]';
  }

  return isHovered
    ? 'border-indigo-200 bg-indigo-100'
    : 'border-indigo-200 bg-white';
};

const SummaryErrorHeroIcon = () => {
  return (
    <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-[#FEE1E1]">
      <DashboardSummaryErrorDocumentIconSvg />
    </div>
  );
};

const SummaryDialogHeader = () => {
  const language = useLanguage();

  return (
    <div className="flex w-full items-center gap-8">
      <div className="min-w-0 flex-1">
        <S2Typography
          as="h3"
          translatedContent={language.dashboardSummaryTitle()}
          variant="headline-6-alt"
          textColorVariant="gray-800"
        />
        <S2Typography
          as="p"
          translatedContent={language.dashboardSummaryDescription()}
          variant="body-7"
          textColorVariant="gray-700"
        />
      </div>
      <DashboardSummaryModalIconSvg svg={{ className: 'h-14 w-14 shrink-0' }} />
    </div>
  );
};

const SummaryContextField = ({
  context,
  maxLength,
  onContextChange,
}: {
  context: string;
  maxLength: number;
  onContextChange: (value: string) => void;
}) => {
  const textareaId = React.useId();
  const language = useLanguage();

  return (
    <div className="flex w-full flex-1 flex-col gap-1">
      <label htmlFor={textareaId}>
        <S2Typography
          as="span"
          translatedContent={language.dashboardSummaryContextLabel()}
          variant="body-7"
          textColorVariant="gray-900"
        />
      </label>
      <div className="relative">
        <textarea
          id={textareaId}
          value={context}
          maxLength={maxLength}
          placeholder={language.dashboardSummaryContextPlaceholder()}
          className="form-textarea min-h-[129px] w-full resize-none rounded border border-gray-200 px-3 py-2 pr-14 text-xs leading-[21px] text-gray-900 placeholder:text-gray-500 focus:border-indigo-200 focus:ring-2 focus:ring-indigo-200"
          onChange={(event) => {
            onContextChange(event.currentTarget.value);
          }}
        />
        <div className="pointer-events-none absolute bottom-2 right-3">
          <S2Typography
            as="span"
            translatedContent={`${context.length}/${maxLength}`}
            variant="body-7"
            textColorVariant="gray-700"
          />
        </div>
      </div>
      <p className="font-sans text-xxs font-normal leading-[18px] tracking-[-0.12px] text-gray-700">
        {language.dashboardSummaryHelperText()}
      </p>
    </div>
  );
};

const SummaryFormPanel = ({
  context,
  maxLength,
  onContextChange,
  onSubmit,
}: {
  context: string;
  maxLength: number;
  onContextChange: (value: string) => void;
  onSubmit: () => void;
}) => {
  const language = useLanguage();

  return (
    <Card
      className="flex h-[400px] w-[360px] flex-col gap-6"
      paddingClassName="p-5"
      roundedClassName="rounded-lg"
      shadowClassName="shadow-soft-xl"
    >
      <SummaryDialogHeader />
      <SummaryContextField
        context={context}
        maxLength={maxLength}
        onContextChange={onContextChange}
      />
      <PrimaryButtonLightBg
        className="w-full"
        innerClassName="w-full justify-center rounded"
        translatedContent={language.dashboardSummarySubmitLabel()}
        onClick={onSubmit}
        disabled={false}
      />
    </Card>
  );
};

const SummaryLoadingPanel = ({
  loadingStatus,
  onCancel,
}: {
  loadingStatus: DashboardAISummaryLoadingStatus;
  onCancel: () => void;
}) => {
  const language = useLanguage();
  const title =
    loadingStatus === 'generating-report'
      ? language.dashboardSummaryGeneratingReportTitle()
      : loadingStatus === 'generating-summary'
        ? language.dashboardSummaryGeneratingSummaryTitle()
        : language.dashboardSummaryPendingTitle();

  return (
    <Card
      className="flex h-[400px] w-[360px] flex-col gap-6"
      paddingClassName="p-5"
      roundedClassName="rounded-lg"
      shadowClassName="shadow-soft-xl"
    >
      <div className="flex flex-1 flex-col items-center justify-center gap-10 px-4 py-6 text-center">
        <Spinner size={76} textColor="text-royal-800" />
        <div className="space-y-2">
          <S2Typography
            as="h3"
            translatedContent={title}
            variant="headline-6-alt"
            textColorVariant="gray-700"
          />
          <S2Typography
            as="p"
            translatedContent={language.dashboardSummaryLoadingDescription()}
            variant="body-7"
            textColorVariant="gray-700"
          />
        </div>
      </div>
      <SecondaryButtonLightBg
        className="w-full"
        translatedContent={language.cancel()}
        onClick={onCancel}
        disabled={false}
      />
    </Card>
  );
};

const SummaryErrorPanel = ({
  errorStatus,
  onRetry,
  onStartOver,
}: {
  errorStatus: DashboardAISummaryFailureStatus;
  onRetry: () => void;
  onStartOver: () => void;
}) => {
  const language = useLanguage();
  const title =
    errorStatus === 'failed-generating-summary'
      ? language.dashboardSummaryOpenAIErrorTitle()
      : language.dashboardSummaryErrorTitle();
  const description =
    errorStatus === 'failed-generating-summary'
      ? language.dashboardSummaryOpenAIErrorDescription()
      : language.dashboardSummaryErrorDescription();

  return (
    <Card
      className="flex h-[400px] w-[360px] flex-col"
      paddingClassName="p-5"
      roundedClassName="rounded-lg"
      shadowClassName="shadow-soft-xl"
    >
      <div className="flex flex-1 flex-col items-center justify-center gap-6 text-center">
        <SummaryErrorHeroIcon />
        <div className="space-y-1 px-10">
          <p className="font-serif text-[20px] leading-normal text-black">
            {title}
          </p>
          <S2Typography
            as="p"
            translatedContent={description}
            variant="body-7"
            textColorVariant="gray-700"
          />
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <PrimaryButtonLightBg
          className="w-full"
          innerClassName="w-full justify-center rounded"
          translatedContent={language.dashboardSummaryRetryLabel()}
          onClick={onRetry}
          disabled={false}
        />
        <TouchTarget
          className="w-full"
          behavior={{
            type: 'onClick',
            onClick: onStartOver,
          }}
          content={() => {
            return (
              <div className="w-full text-center">
                <S2Typography
                  as="span"
                  translatedContent={language.dashboardSummaryStartOverLabel()}
                  variant="body-7-bold"
                  textColorVariant="royal-800"
                />
              </div>
            );
          }}
        />
      </div>
    </Card>
  );
};
